import { useEffect, useState, type ReactNode } from "react";
import { App } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { CloseCircleOutlined, UndoOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { OpenTable, Reservation, ReservationAction, ReservationsDay } from "../../../api/reservations";
import { useReservationAction } from "../../../hooks/useReservations";
import { getApiErrorMessage } from "../../../utils/translations";
import { formatClock } from "../logic/dayTime";
import { reservationBillIds, reservationStartMs } from "../logic/tableStatus";
import {
  seatDecisionOf,
  seatPickBillsOf,
  unseatClosingBillsOf,
  type UpcomingActions,
} from "../logic/reservationActions";
import { canConfirmSeatBills, seatBillsChoiceOf, separatePlanOf, type SeatBillsMode } from "../logic/seatBills";
import { useReservationFormat } from "./useReservationFormat";
import SeatBillPickerModal from "../components/seat/SeatBillPickerModal";
import SeatBillsModal from "../components/seat/SeatBillsModal";
import ReservationConfirmModal, { type ConfirmTone } from "../components/confirm/ReservationConfirmModal";

const SUCCESS_KEY: Record<ReservationAction, string> = {
  seat: "reservations.messages.seated",
  release: "reservations.messages.released",
  cancel: "reservations.messages.cancelled",
  unseat: "reservations.messages.unseated",
  restore: "reservations.messages.restored",
};

/**
 * Съобщението за успех: при закачане — при кой сервитьор; при нова сметка —
 * че масата ще е на сервитьора, който поръча първи; при отделна сметка за
 * всяка маса — масите; при „Гостите си тръгнаха" — дали масата остава заета.
 */
const successMessageOf = (
  action: ReservationAction,
  attachedTo: OpenTable | null,
  newBill: boolean,
  billStaysOpen: boolean,
  // „Отмени настаняването" изтри празните сметки; масите им — ако са известни.
  billClosed: boolean,
  closedBillTables: string[],
  // „Отделна сметка за всяка маса" — масите („1, 2 и 3"); null — една сметка.
  separateTables: string | null,
): [string, Record<string, string>?] => {
  if (action === "seat" && separateTables !== null) {
    return ["reservations.messages.seatedSeparate", { tables: separateTables }];
  }
  if (action === "seat" && attachedTo) {
    return attachedTo.waiterName
      ? ["reservations.messages.seatedAttached", { waiter: attachedTo.waiterName }]
      : ["reservations.messages.seatedAttachedNoWaiter"];
  }
  if (action === "seat") {
    return newBill ? ["reservations.messages.seatedNewBill"] : ["reservations.messages.seatedAttachedNoWaiter"];
  }
  if (action === "release" && billStaysOpen) return ["reservations.messages.releasedBillOpen"];
  if (action === "unseat" && billClosed) {
    if (closedBillTables.length > 1) {
      return ["reservations.messages.unseatedBillsClosed", { tables: closedBillTables.join(", ") }];
    }
    return closedBillTables.length === 1
      ? ["reservations.messages.unseatedBillClosed", { table: closedBillTables[0] }]
      : ["reservations.messages.unseatedBillClosedNoTable"];
  }
  return [SUCCESS_KEY[action]];
};

/** „Върни" на отказана, чийто час вече е минал: BE я връща направо като
 * „Неизпълнена" (`expired`), не в „Предстоящи". */
const restoredAsExpired = (action: ReservationAction, updated: Reservation) =>
  action === "restore" && updated.status === "expired";

// Действията с потвърждение („Да откажем ли резервацията?" / „Да отменим ли
// настаняването?"): текстовете, иконата на чипа и тонът на бутона.
type ConfirmedAction = Extract<ReservationAction, "cancel" | "unseat">;

// И двете връщат нещо назад (отказ / отмяна) — плътен червен бутон.
const CONFIRM_TEXTS: Record<ConfirmedAction, { keys: string; tone: ConfirmTone; icon: ReactNode }> = {
  cancel: { keys: "reservations.confirmCancel", tone: "danger", icon: <CloseCircleOutlined /> },
  unseat: { keys: "reservations.confirmUnseat", tone: "danger", icon: <UndoOutlined /> },
};

interface ConfirmRequest {
  reservation: Reservation;
  action: ConfirmedAction;
  resolve: (result: Reservation | null) => void;
}

/**
 * След „Гостите си тръгнаха" сметката (някоя от сметките) на резервацията
 * още е отворена — резервациите никога не закриват сметки, масата остава
 * заета до закриването ѝ в Маркиране. Мутацията изчаква опресняването на
 * резервациите (`onSettled`), затова кешът вече е пресен.
 */
const isBillStillOpen = (days: [unknown, ReservationsDay | undefined][], billIds: number[]) =>
  days.some(([, day]) => day?.openTables.some((bill) => billIds.includes(bill.billId)));

/** Отворените сметки от всички заредени дни (кешът на резервациите). */
const cachedOpenTables = (days: [unknown, ReservationsDay | undefined][]): OpenTable[] =>
  days.flatMap(([, day]) => day?.openTables ?? []);

/** Изборът „Една сметка / Отделна сметка за всяка маса" в ход. */
interface SeatBillsRequest {
  reservation: Reservation;
  mode: SeatBillsMode;
  // Сметката от „При кой сервитьор" вътре в „Една сметка".
  pickedBillId: number | null;
}

/**
 * Действията по резервация за поповъра и списъка: Настани (резервация с
 * няколко маси — първо „Една сметка или по една на маса?", който поема и
 * „при кой сервитьор"; резервация с една маса и няколко отворени сметки на
 * нея — модалът „При кой сервитьор да настаним?"), Гостите си тръгнаха,
 * Откажи (с потвърждение), Отмени настаняването (с потвърждение), Върни.
 * Никое не закрива сметка — освен отмяната, при която BE премахва празните
 * сметки от самото „Настани".
 * Грешките от BE (409 „Маса 4 вече е заета"…) се показват с текста им.
 * `loadSeat` — опреснява деня и връща пресните действия на резервацията:
 * „Настани" решава по тях, не по кеша (сметка, отворена след последния
 * poll, иначе остава невидима и резервацията се закача към единствената
 * позната, без да пита). `seatBillsOf` — ТЕКУЩИТЕ кандидати за закачане на резервацията (от кеша,
 * който се опреснява на 15 s): модалът ги чете при всеки рендер, не пази
 * снимка от отварянето. `onSeated` — след успешно настаняване
 * (екранът връща линията към „сега"); `onBackToUpcoming` — резервацията пак е
 * предстояща (Върни / Отмени настаняването): табът „Предстоящи" и картата
 * подчертана. `isEdgeLocal` — локален режим срещу касата: „Отделна сметка
 * за всяка маса" е изключена (касата отваря една сметка).
 */
export const useReservationActions = ({
  nowMs,
  loadSeat,
  seatBillsOf,
  onSeated,
  onBackToUpcoming,
  isEdgeLocal = false,
}: {
  // „Сега" на страницата — за „отворена преди 25 мин" в модала за сервитьора.
  nowMs: number;
  // Пресните действия на резервацията (null — вече не е предстояща).
  loadSeat: (reservation: Reservation) => Promise<UpcomingActions | null>;
  seatBillsOf: (reservation: Reservation) => OpenTable[];
  onSeated?: () => void;
  onBackToUpcoming?: (reservation: Reservation) => void;
  isEdgeLocal?: boolean;
}) => {
  const { t } = useTranslation();
  const format = useReservationFormat();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const mutation = useReservationAction();
  // Модалът „При кой сервитьор да настаним?": само резервацията — кандидатите
  // се смятат наново при всеки рендер (сметка може да се закрие или да се
  // закачи към друга резервация, докато модалът е отворен).
  const [pickFor, setPickFor] = useState<Reservation | null>(null);
  // „Настани" опреснява деня преди решението — резервацията, чиято проверка тече.
  const [checkingSeatId, setCheckingSeatId] = useState<number | null>(null);
  // Отвореното потвърждение (Откажи / Отмени настаняването) — модалът се
  // рендира веднъж в страницата, обещанието се решава от бутоните му.
  const [confirming, setConfirming] = useState<ConfirmRequest | null>(null);
  const pickBills = pickFor ? seatPickBillsOf(seatBillsOf(pickFor)) : [];
  // Останаха 0–1 кандидата → изборът вече няма смисъл: модалът се затваря
  // (хостесата натиска „Настани" наново — нова сметка или директно закачане).
  const pickObsolete = pickFor !== null && pickBills.length === 0 && !mutation.isPending;
  useEffect(() => {
    if (pickObsolete) setPickFor(null);
  }, [pickObsolete]);
  // „Една сметка или по една на маса?" — изборът се смята наново при всеки
  // рендер от текущите кандидати (както „При кой сервитьор").
  const [billsRequest, setBillsRequest] = useState<SeatBillsRequest | null>(null);
  const billsChoice = billsRequest
    ? seatBillsChoiceOf(billsRequest.reservation, seatBillsOf(billsRequest.reservation), isEdgeLocal)
    : null;

  const readDays = () => queryClient.getQueriesData<ReservationsDay>({ queryKey: ["reservations"] });

  const run = async (
    reservation: Reservation,
    action: ReservationAction,
    attachTo: OpenTable | null = null,
    separateBills = false,
  ) => {
    // Масите на празните сметки, които отмяната ще затвори — от кеша ПРЕДИ
    // заявката (след нея сметките вече ги няма в `openTables`).
    const closingBills =
      action === "unseat" ? unseatClosingBillsOf(reservation, cachedOpenTables(readDays())) : [];
    try {
      const updated = await mutation.mutateAsync({
        id: reservation.id,
        action,
        billId: attachTo?.billId,
        ...(separateBills ? { separateBills: true } : {}),
      });
      const billStaysOpen =
        action === "release" &&
        isBillStillOpen(readDays(), reservationBillIds(updated));
      const newBill = action === "seat" && updated.billCreated === true;
      if (restoredAsExpired(action, updated)) {
        message.info(t("reservations.messages.restoredExpired"));
        return updated;
      }
      // BE решава окончателно (`billClosed`); кешът дава само името на масата.
      const billClosed = action === "unseat" && updated.billClosed === true;
      const closedBillTables = !billClosed
        ? []
        : closingBills.length > 0
          ? closingBills.map((bill) => bill.tableName)
          : reservation.tableNames.slice(0, 1);
      const separateTables =
        action === "seat" && separateBills ? format.list(updated.tableNames) : null;
      const [key, params] = successMessageOf(
        action, attachTo, newBill, billStaysOpen, billClosed, closedBillTables, separateTables,
      );
      message.success(t(key, params));
      if (action === "seat") onSeated?.();
      if (action === "restore" || action === "unseat") onBackToUpcoming?.(updated);
      return updated;
    } catch (error) {
      message.error(getApiErrorMessage(error, t("reservations.messages.actionFailed")));
      return null;
    }
  };

  /**
   * „Откажи" / „Отмени настаняването" — първо потвърждението (общият шел на
   * модалите). „Назад"/Esc/фонът → null; действието → резултатът на `run`
   * (null и при грешка — тя е показана като съобщение). Ново потвърждение,
   * докато тече друго, приключва старото с null.
   */
  const confirm = (reservation: Reservation, action: ConfirmedAction) =>
    new Promise<Reservation | null>((resolve) => {
      confirming?.resolve(null);
      setConfirming({ reservation, action, resolve });
    });

  // „Отмени настаняването" откача резервацията; празната сметка от самото
  // „Настани" (никой не е поръчал) BE премахва, всяка друга остава в Маркиране.
  const confirmCancel = (reservation: Reservation) => confirm(reservation, "cancel");
  const confirmUnseat = (reservation: Reservation) => confirm(reservation, "unseat");

  const finishConfirm = async (request: ConfirmRequest, confirmed: boolean) => {
    const result = confirmed ? await run(request.reservation, request.action) : null;
    setConfirming((current) => (current === request ? null : current));
    request.resolve(result);
  };

  const confirmTexts = confirming ? CONFIRM_TEXTS[confirming.action] : null;
  // Отмяна, която ще затвори празните сметки — друг текст в потвърждението.
  const unseatClosingBills =
    confirming?.action === "unseat" ? unseatClosingBillsOf(confirming.reservation, cachedOpenTables(readDays())) : [];
  const confirmContentKey =
    unseatClosingBills.length > 1 ? "contentBillsClose" : unseatClosingBills.length === 1 ? "contentBillCloses" : "content";
  const confirmModal = (
    <ReservationConfirmModal
      open={confirming !== null}
      tone={confirmTexts?.tone ?? "primary"}
      icon={confirmTexts?.icon}
      title={confirmTexts ? t(`${confirmTexts.keys}.title`) : ""}
      text={
        confirming && confirmTexts
          ? t(`${confirmTexts.keys}.${confirmContentKey}`, {
              guest: confirming.reservation.guestName,
              billTable: unseatClosingBills[0]?.tableName ?? "",
              billTables: unseatClosingBills.map((bill) => bill.tableName).join(", "),
              time: formatClock(reservationStartMs(confirming.reservation)),
              tables: confirming.reservation.tableNames.join(", "),
              count: confirming.reservation.tableNames.length,
            })
          : ""
      }
      confirmLabel={confirmTexts ? t(`${confirmTexts.keys}.ok`) : ""}
      backLabel={confirmTexts ? t(`${confirmTexts.keys}.back`) : ""}
      pending={confirming !== null && mutation.isPending}
      onConfirm={() => {
        if (confirming) void finishConfirm(confirming, true);
      }}
      onBack={() => {
        if (confirming) void finishConfirm(confirming, false);
      }}
    />
  );

  /**
   * „Настани" — ЕДИНСТВЕНИЯТ вход към seat (картата, поповърът на масата,
   * „Резервация в този час"): първо опреснява деня, после по пресните данни —
   * спрян (съобщение защо); резервация с няколко маси — изборът „Една
   * сметка / Отделна сметка за всяка маса" (той поема и „при кой
   * сервитьор"); иначе без сметка — нова, с една — закача към нея (billId
   * се праща изрично), с няколко — модалът за избор на сервитьор.
   */
  const seat = async (reservation: Reservation) => {
    if (checkingSeatId !== null || mutation.isPending) return null;
    setCheckingSeatId(reservation.id);
    let available: UpcomingActions | null;
    try {
      available = await loadSeat(reservation);
    } catch (error) {
      message.error(getApiErrorMessage(error, t("reservations.messages.actionFailed")));
      return null;
    } finally {
      setCheckingSeatId(null);
    }
    const decision = seatDecisionOf(available);
    if (decision.kind === "blocked") {
      message.warning(decision.hint ? t(decision.hint.key, decision.hint.params) : t("reservations.messages.seatUnavailable"));
      return null;
    }
    if (available && seatBillsChoiceOf(reservation, available.seatBills, isEdgeLocal)) {
      setBillsRequest({ reservation, mode: "single", pickedBillId: null });
      return null;
    }
    if (decision.kind === "pick") {
      setPickFor(reservation);
      return null;
    }
    return run(reservation, "seat", decision.kind === "attach" ? decision.bill : null);
  };

  // „Настани" от избора „Една сметка / Отделна…". `rowBill` — сметка,
  // избрана с Enter върху реда ѝ (още не е в състоянието). При грешка (409)
  // модалът остава отворен с опреснения избор.
  const confirmSeatBills = async (rowBill?: OpenTable) => {
    if (!billsRequest || !billsChoice || mutation.isPending) return;
    const { single } = billsChoice;
    const { mode } = billsRequest;
    const pickedBill =
      single.kind === "pick"
        ? single.bills.find((bill) => bill.billId === (rowBill?.billId ?? billsRequest.pickedBillId)) ?? null
        : null;
    if (!canConfirmSeatBills(billsChoice, mode, pickedBill)) return;
    // „Отделна…" праща избраната само при маса с няколко сметки (`owner`).
    const separatePlan = mode === "separate" ? separatePlanOf(billsChoice, pickedBill) : null;
    const attachTo = separatePlan
      ? separatePlan.kind === "ready"
        ? separatePlan.owner
        : null
      : single.kind === "attach"
        ? single.bill
        : pickedBill;
    if (await run(billsRequest.reservation, "seat", attachTo, mode === "separate")) setBillsRequest(null);
  };

  // При грешка (409 — сметката току-що е закрита или взета) модалът остава
  // отворен с опреснения списък.
  const pickBill = async (bill: OpenTable) => {
    if (!pickFor) return;
    if (await run(pickFor, "seat", bill)) setPickFor(null);
  };

  const pendingBillId =
    mutation.isPending && mutation.variables?.action === "seat" ? mutation.variables.billId ?? null : null;

  const billPicker = (
    <>
      <SeatBillPickerModal
        reservation={pickBills.length > 0 ? pickFor : null}
        bills={pickBills}
        nowMs={nowMs}
        pendingBillId={pickFor ? pendingBillId : null}
        onPick={(bill) => void pickBill(bill)}
        onClose={() => {
          if (!mutation.isPending) setPickFor(null);
        }}
      />
      <SeatBillsModal
        reservation={billsChoice ? billsRequest?.reservation ?? null : null}
        choice={billsChoice}
        mode={billsRequest?.mode ?? "single"}
        pickedBillId={billsRequest?.pickedBillId ?? null}
        nowMs={nowMs}
        pending={billsRequest !== null && mutation.isPending}
        onModeChange={(mode) => setBillsRequest((current) => (current ? { ...current, mode } : current))}
        onPickBill={(bill) =>
          setBillsRequest((current) => (current ? { ...current, pickedBillId: bill.billId } : current))
        }
        onConfirm={(rowBill) => void confirmSeatBills(rowBill)}
        onBack={() => setBillsRequest(null)}
      />
    </>
  );

  const checkingSeat = checkingSeatId !== null;
  return {
    isPending: mutation.isPending || checkingSeat,
    // Кое действие е в ход (за спинера на точния бутон); проверката преди
    // „Настани" се брои за seat.
    pendingAction: checkingSeat ? ("seat" as const) : mutation.isPending ? mutation.variables?.action ?? null : null,
    // Резервацията, по която е действието (за спинера на точната карта).
    pendingId: checkingSeat ? checkingSeatId : mutation.isPending ? mutation.variables?.id ?? null : null,
    seat,
    // Модалите „Една сметка или по една на маса?" и „При кой сервитьор да
    // настаним?" — рендират се веднъж в страницата.
    billPicker,
    // Модал е отворен (Esc на страницата не бива да затваря и поповъра).
    isPickingBill: pickFor !== null || billsRequest !== null,
    // Потвърждението („Откажи" / „Отмени настаняването") — рендира се веднъж в страницата.
    confirmModal,
    isConfirming: confirming !== null,
    release: (reservation: Reservation) => run(reservation, "release"),
    cancel: confirmCancel,
    unseat: confirmUnseat,
    restore: (reservation: Reservation) => run(reservation, "restore"),
  };
};

export type ReservationActions = ReturnType<typeof useReservationActions>;
